<template>
  <ModuleWrapper :moduleId="moduleId" title="Buffer Video" @initializeData="initializeData">
    <template #main>
      <div class="tw-mb-2 tw-block tw-text-xs">
        Plays on stage before the event begins and between sessions, if no other sessions are live
      </div>
      <StudioSpinner v-if="isLoading" :coverContainer="false" size="xsmall" />
      <button
        v-else-if="!selectedVideoAsset"
        class="tw-flex tw-items-center tw-text-deep-orange-600 hover:tw-text-deep-orange-500"
        @click="showVideoLibraryDialog = true"
      >
        <PlusCircle class="tw-mr-1 tw-h-6 tw-w-6" />
        <span class="tw-text-xs tw-font-medium">Select Video</span>
      </button>
      <VideoLibraryListItem
        v-else
        :visibleActions="{
          openPreview: true,
          rename: true,
          remove: true
        }"
        alwaysShowActions
        :videoAsset="selectedVideoAsset"
        @remove="handleDeselectVideoAsset"
        @rename-success="handleRenameSuccess($event)"
      />
      <VideoLibraryDialog
        v-if="showVideoLibraryDialog"
        title="Select Buffer Video"
        :maxSelections="1"
        @close="showVideoLibraryDialog = false"
        @select-videos="handleSelectVideos"
      />
    </template>
  </ModuleWrapper>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';

import ModuleWrapper from './ModuleWrapper.vue';

import VideoLibraryDialog from '@/studio/videoLibrary/VideoLibraryDialog.vue';
import VideoLibraryListItem from '@/studio/videoLibrary/VideoLibraryListItem.vue';
import { VideoAsset, getVideoAsset } from '@/studio/videoLibrary/videoLibraryService';
import PlusCircle from '@/studioComponents/Icons/PlusCircle.vue';
import StudioSpinner from '@/studioComponents/StudioSpinner.vue';

export default defineComponent({
  name: 'BufferVideo',
  components: {
    ModuleWrapper,
    VideoLibraryDialog,
    VideoLibraryListItem,
    StudioSpinner,
    PlusCircle
  },
  props: {
    moduleId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      selectedVideoAsset: undefined as VideoAsset | undefined,
      showVideoLibraryDialog: false
    };
  },
  mounted() {
    this.initializeData();
  },
  computed: {
    ...mapGetters('videos', {
      bufferVideos: 'getVideoAssets'
    }),
    ...mapGetters('branding', {
      getInitialModuleData: 'getInitialModuleData'
    })
  },
  methods: {
    ...mapMutations('branding', {
      setChangesMadeInEvent: 'setChangesMadeInEvent'
    }),
    ...mapActions('studioToast', { triggerToast: 'triggerToast' }),
    initializeData() {
      const moduleKeys = ['stage_roll_video_asset'];
      const { stage_roll_video_asset } = this.getInitialModuleData(moduleKeys);

      if (stage_roll_video_asset) {
        this.isLoading = true;
        getVideoAsset(stage_roll_video_asset)
          .then(videoAsset => {
            this.selectedVideoAsset = videoAsset;
          })
          .catch(() => {
            this.triggerToast({
              msg: 'The buffer video could not be loaded.',
              mode: 'error'
            });
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    handleSelectVideos(videoAssets: VideoAsset[]) {
      this.selectedVideoAsset = videoAssets[0];
      this.showVideoLibraryDialog = false;
      this.setChangesMadeInEvent({
        stage_roll_video_asset: this.selectedVideoAsset.id
      });
    },
    handleDeselectVideoAsset() {
      this.selectedVideoAsset = undefined;
      this.setChangesMadeInEvent({ stage_roll_video_asset: null });
    },
    handleRenameSuccess(video: VideoAsset) {
      this.selectedVideoAsset = video;
    }
  }
});
</script>
